import { Grid, Typography, Tooltip, IconButton } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { useFormikContext } from 'formik'
import { MdDelete } from 'react-icons/md'
import { currencyMask } from '../../../../utils/masks'
import NumberSpinner from '../../pontoDeVenda/qunatidadeProdutoTable'
import CurrencyField from '../../../../components/CurrencyField/CurrencyField'
import AutoComplete from '../../../../components/AutoComplete/AutoComplete'
import { useCheckout } from './CheckoutContext'
import CardTable from '../../../../components/CardTable'

const nomeCell = ({ linhaOriginal }) => (
  <Grid container>
    <Grid item xs={12}>
      <Tooltip title={linhaOriginal?.dsProduto}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {linhaOriginal?.dsProduto}
        </Typography>
      </Tooltip>
    </Grid>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        R$: {currencyMask(linhaOriginal?.vlUnitarioInicial || 0)}
      </Typography>
    </Grid>
  </Grid>
)

const ESTILO_COL = [
  { width: '50%', truncate: true },
  { width: '15%' },
  { width: '15%', textAlign: 'left' },
  { width: '15%', textAlign: 'right' },
  { width: '5%', textAlign: 'center' },
]

const ESTILO_COL_SEM_EXCLUSAO = [
  { width: '50%', truncate: true },
  { width: '15%' },
  { width: '10%', textAlign: 'left' },
  { width: '25%', textAlign: 'right' },
]

const VendaCheckoutCard = () => {
  const [textFieldAc, setTextFieldAc] = useState('')

  const { isSubmitting, values, setFieldValue } = useFormikContext()
  const {
    isFinalizado,
    listaProdutosAdicionais,
    listaProdutos,
    listaTerapias,
    addProdutoAdicional,
    removeProdutoAdicional,
    updateProdutoAdicional,
  } = useCheckout()

  const isDisabled = isSubmitting || isFinalizado

  const itensFinalizados = useMemo(
    () =>
      isFinalizado
        ? [...listaProdutos, ...listaTerapias].filter(
            (item) => item.tpOrigem === 'V' && String(item.tpPedido) === '2',
          )
        : [],
    [isFinalizado, listaProdutos, listaTerapias],
  )

  const mergedList = useMemo(
    () => [...listaProdutosAdicionais, ...itensFinalizados],
    [listaProdutosAdicionais, itensFinalizados],
  )

  const totalCombinado = useMemo(
    () =>
      itensFinalizados.reduce((acc, item) => acc + (item.vlTotal || 0), 0) +
      listaProdutosAdicionais.reduce(
        (acc, item) => acc + Number(item.vlTotal || 0),
        0,
      ),
    [itensFinalizados, listaProdutosAdicionais],
  )

  const handleQtdAdicional = useCallback(
    (item) => (quantidade) => {
      updateProdutoAdicional(item.idx, {
        qtProduto: quantidade,
        vlTotal: quantidade * item.vlUnitario,
      })
    },
    [updateProdutoAdicional],
  )

  const handleUnitarioAdicional = useCallback(
    (item) => (valor) => {
      const vl = Number(valor) || 0
      updateProdutoAdicional(item.idx, {
        vlUnitario: vl,
        vlTotal: vl * (item.qtProduto || 1),
      })
    },
    [updateProdutoAdicional],
  )

  const opcoesCol = useCallback(
    (disabled, somenteLeitura) => [
      { alias: 0, customJsx: nomeCell },
      {
        alias: 1,
        customJsx: ({ linhaOriginal }) =>
          somenteLeitura ? (
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}
            >
              {linhaOriginal?.qtProduto || 0}
            </Typography>
          ) : (
            <NumberSpinner
              size="small"
              disabled={disabled}
              onChange={handleQtdAdicional(linhaOriginal)}
              value={linhaOriginal?.qtProduto}
              min={0}
            />
          ),
      },
      {
        alias: 2,
        customJsx: ({ linhaOriginal }) =>
          somenteLeitura ? (
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}
            >
              R$ {currencyMask(linhaOriginal?.vlUnitario || 0)}
            </Typography>
          ) : (
            <CurrencyField
              size="small"
              variant="outlined"
              sx={{ minWidth: 0, maxWidth: '100%' }}
              disabled={disabled}
              value={linhaOriginal?.vlUnitario || 0}
              onChange={handleUnitarioAdicional(linhaOriginal)}
            />
          ),
      },
      ...(!somenteLeitura
        ? [
            {
              alias: 4,
              customJsx: ({ linhaOriginal }) => (
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => removeProdutoAdicional(linhaOriginal?.idx)}
                >
                  <MdDelete fontSize={16} />
                </IconButton>
              ),
            },
          ]
        : []),
    ],
    [removeProdutoAdicional, handleQtdAdicional, handleUnitarioAdicional],
  )

  return (
    <CardTable
      titulo="Venda no Check-out"
      dadosTabela={mergedList}
      colunas={
        isFinalizado
          ? ['Produto', 'Quantidade', 'Unitário', 'Valor Total']
          : ['Produto', 'Quantidade', 'Unitário', 'Valor Total', '']
      }
      mapear={(item) => {
        const valores = [
          item.dsProduto,
          item.qtProduto ?? 1,
          item.vlUnitario,
          item.vlTotal,
        ]
        if (!isFinalizado) valores.push('')
        return valores
      }}
      colunasMoeda={[2, 3]}
      estiloColunas={isFinalizado ? ESTILO_COL_SEM_EXCLUSAO : ESTILO_COL}
      opcoesColuna={opcoesCol(isDisabled, isFinalizado)}
      maxLinhas={3}
      alturaMinimaLinha={0}
      preencherAltura
      preencherCorpo
      rotuloTotal="Total Produtos Adicionais"
      valorTotal={totalCombinado}
      semBordas
      conteudoTopo={
        !isFinalizado ? (
          <AutoComplete
            fullWidth
            getOptionLabel={(v) => v?.[1]}
            InputLabelProps={{ shrink: true }}
            label="Adicionar Produto"
            metodo="buscaProdutoTerapiaPontoVenda"
            disabled={isDisabled}
            value={values?.acProduto}
            textFieldProps={{
              onChange: (v) => setTextFieldAc(v.target.value),
            }}
            onChange={(_, produto) => {
              setTextFieldAc('')
              if (produto?.[0]?.[0]) {
                addProdutoAdicional(produto)
                setFieldValue('acProduto', null)
              }
            }}
            inputValueAc={textFieldAc}
          />
        ) : null
      }
    />
  )
}

export default VendaCheckoutCard
